import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Heart, ArrowLeft, Check, Sparkles } from 'lucide-react';

const TIERS = [
  {
    id: 'personal',
    name: 'Personal',
    price: '$7.75',
    currency: 'AUD',
    tributes: '+1 tribute',
    description: 'For an individual tribute',
    features: [
      'Up to 20 images per tribute',
      'Up to 4 videos per tribute',
      'Invite up to 4 contributors',
      'PDF export & templates',
      'Stackable — buy multiple'
    ],
    popular: false
  },
  {
    id: 'family',
    name: 'Family',
    price: '$19.75',
    currency: 'AUD',
    tributes: '+5 tributes',
    description: 'Honour family and friends',
    features: [
      'Up to 20 images per tribute',
      'Up to 4 videos per tribute',
      'Invite up to 20 contributors',
      'PDF export & templates',
      'Stackable — buy multiple'
    ],
    popular: true
  },
  {
    id: 'legacy',
    name: 'Legacy',
    price: '$38.75',
    currency: 'AUD',
    tributes: '+10 tributes',
    description: 'Preserve lasting legacies',
    features: [
      'Up to 20 images per tribute',
      'Up to 4 videos per tribute',
      'Invite unlimited contributors',
      'PDF export & templates',
      'Stackable — buy multiple'
    ],
    popular: false
  }
];

export default function PricingPage() {
  const { user } = useAuth();

  return (
    <div className="min-h-screen bg-[#FDFBF7]">
      <div className="fixed inset-0 pointer-events-none overflow-hidden">
        <div className="absolute top-0 left-1/4 w-[500px] h-[500px] bg-purple-100/40 rounded-full blur-[120px]" />
        <div className="absolute bottom-0 right-1/3 w-[400px] h-[400px] bg-pink-100/40 rounded-full blur-[100px]" />
      </div>

      <header className="sticky top-0 z-50 bg-[#FDFBF7]/90 backdrop-blur-xl border-b border-[#1a1a2e]/5">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center justify-between">
          <Link to={user ? '/dashboard' : '/'} className="flex items-center gap-2 text-[#1a1a2e]/50 hover:text-[#1a1a2e]" data-testid="back-link">
            <ArrowLeft className="w-4 h-4" /><span className="text-sm">{user ? 'Dashboard' : 'Home'}</span>
          </Link>
          <Link to="/" className="flex items-center gap-2.5">
            <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-[#7c3aed] to-[#db2777] flex items-center justify-center shadow-lg shadow-purple-500/20">
              <Heart className="w-4 h-4 text-white" strokeWidth={2} fill="currentColor" />
            </div>
            <span className="text-xl font-bold text-[#1a1a2e]">iExtol</span>
          </Link>
          <div />
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-16 relative z-10">
        <div className="text-center mb-16">
          <Badge className="bg-purple-100 text-[#7c3aed] border-purple-200 border rounded-full mb-4 px-4 py-1">Pricing</Badge>
          <h1 className="text-4xl sm:text-5xl font-bold text-[#1a1a2e] tracking-tight mb-4" data-testid="pricing-heading">
            Create more tributes
          </h1>
          <p className="text-lg text-[#1a1a2e]/50 max-w-xl mx-auto">
            Your first tribute is always free. Add more tributes for the people who shaped your life.
          </p>
          <p className="text-[#1a1a2e]/40 text-sm mt-2">One-off payment. No subscriptions. Packages are stackable.</p>
        </div>

        <div className="grid md:grid-cols-3 gap-6">
          {TIERS.map(tier => (
            <Card key={tier.id} className={`border-[#1a1a2e]/5 bg-white shadow-lg relative overflow-hidden ${tier.popular ? 'ring-2 ring-[#7c3aed] shadow-purple-500/10' : ''}`}
              data-testid={`pricing-${tier.id}`}>
              {tier.popular && (
                <div className="absolute top-0 right-0 bg-gradient-to-l from-[#7c3aed] to-[#db2777] text-white text-xs px-4 py-1 rounded-bl-xl font-medium">Most Popular</div>
              )}
              <CardContent className="p-8">
                <div className="bg-gradient-to-r from-[#7c3aed]/10 to-[#db2777]/10 rounded-xl px-4 py-3 mb-4 text-center">
                  <span className="text-lg font-bold text-[#7c3aed]">{tier.tributes}</span>
                </div>
                <h3 className="text-xl font-bold text-[#1a1a2e] mb-1">{tier.name}</h3>
                <p className="text-sm text-[#1a1a2e]/50 mb-4">{tier.description}</p>
                <div className="mb-6">
                  <span className="text-4xl font-bold text-[#1a1a2e]">{tier.price}</span>
                  <span className="text-[#1a1a2e]/40 ml-1">{tier.currency}</span>
                  <span className="text-[#1a1a2e]/30 text-sm ml-2">one-off</span>
                </div>
                <ul className="space-y-3 mb-8">
                  {tier.features.map((f) => (
                    <li key={f} className="flex items-start gap-2 text-sm text-[#1a1a2e]/70">
                      <Check className="w-4 h-4 text-emerald-500 shrink-0 mt-0.5" />
                      {f}
                    </li>
                  ))}
                </ul>
                <Button className={`w-full rounded-full py-6 ${tier.popular
                  ? 'bg-gradient-to-r from-[#7c3aed] to-[#db2777] text-white shadow-lg shadow-purple-500/20'
                  : 'bg-[#1a1a2e] text-white hover:bg-[#2d2d4a]'}`}
                  data-testid={`buy-${tier.id}`}>
                  <Sparkles className="w-4 h-4 mr-2" />
                  Get {tier.name}
                </Button>
              </CardContent>
            </Card>
          ))}
        </div>

        <div className="text-center mt-12">
          <p className="text-[#1a1a2e]/40 text-sm">
            All packages are one-off payments and stackable. For example, buy 2 Personal packages to get 3 tributes total in your dashboard.
          </p>
          <p className="text-[#1a1a2e]/30 text-xs mt-2">Payment integration coming soon. Contact us for early access.</p>
        </div>
      </main>

      <footer className="py-8 bg-[#1a1a2e] mt-16">
        <div className="max-w-7xl mx-auto px-4 text-center">
          <p className="text-white/30 text-sm italic mb-2">"Many people go through life never knowing how they have made a positive impact on someone's life."</p>
          <p className="text-sm text-white/40">&copy; {new Date().getFullYear()} Resilience Community Collective. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
}
